import { Button, Space, Text } from '@billy/design-system'
import React, { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { PeriodMode } from '../../../../../enums/periodMode'
import { PeriodValue } from '../../../../../types/periodValue'
import { usePeriodSelectContext } from '../../contexts/periodSelectContext'
import { CustomDate } from '../CustomDate'
import { PeriodModeToggler } from '../PeriodModeToggler'
import { PeriodsList } from '../PeriodsList'
import * as Styled from './styles'

interface PeriodDropdownProps {
  onSave?: (periodValue?: PeriodValue) => void
}

export const PeriodDropdown = ({ onSave }: PeriodDropdownProps): ReactElement => {
  const {
    setPeriodValueSaved,
    setPeriodValue,
    periodValue,
    periodMode,
    isCustomDate,
    hiddenPeriodModes,
    requireFinitePeriods,
  } = usePeriodSelectContext()
  const { t } = useTranslation()

  /* Applicable for cases requiring both custom dates be present === finite periods */
  const isMissingRequiredCustomDate =
    requireFinitePeriods && isCustomDate && (!periodValue?.customDate?.startDate || !periodValue?.customDate?.endDate)

  const isButtonEnabled = (!!periodValue || periodMode === PeriodMode.All) && !isMissingRequiredCustomDate

  const handleSaveClick = useCallback(() => {
    let periodValueToSave = periodValue

    if (periodMode === PeriodMode.All && !isCustomDate) {
      periodValueToSave = { mode: PeriodMode.All }
    }

    setPeriodValue(periodValueToSave)
    setPeriodValueSaved(periodValueToSave)
    onSave?.(periodValueToSave)
  }, [isCustomDate, onSave, periodMode, periodValue, setPeriodValue, setPeriodValueSaved])

  return (
    <Styled.PeriodDropdownWrapper>
      <Styled.ScrollableWrapper>
        {/* Period Mode Toggler: */}
        <PeriodModeToggler />
        <Space size="s12" />

        {/* Periods navigation and list of items:  */}
        <PeriodsList />
        <Space size="s12" />

        {/* Custom Date: */}
        {!hiddenPeriodModes.includes(PeriodMode.CustomDate) && (
          <>
            <Text colorVariant="label" weight="medium">
              {t('period_select.mode.custom_date')}
            </Text>
            <Space size="s2" />
            <CustomDate />
          </>
        )}
      </Styled.ScrollableWrapper>

      <Styled.FooterWrapper>
        {/* Save Button: */}
        <Button disabled={!isButtonEnabled} onClick={handleSaveClick} fullWidth>
          {t('period_select.save_button')}
        </Button>
      </Styled.FooterWrapper>
    </Styled.PeriodDropdownWrapper>
  )
}
